import { Button } from "@/components/ui/button";
import { ArrowRight, Home, Zap, Sun } from "lucide-react";
import { Link } from "react-router-dom";
import CanonicalTag from "@/components/CanonicalTag";

const MidCenturyModernADUDesignBelmont = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <CanonicalTag url="https://udaadu.com/mid-century-modern-adu-design-belmont" />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-6">
            Mid-Century Modern ADU Design in Belmont
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Embrace iconic design principles in Belmont with mid-century modern ADUs featuring clean lines, open concepts, and seamless indoor-outdoor living.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Your Mid-Century Modern ADU Quote
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg">
              View Mid-Century Portfolio
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-muted/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Mid-Century Modern ADU Design in Belmont?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-lg bg-card border">
              <Home className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Iconic Style</h3>
              <p className="text-muted-foreground">
                Timeless mid-century design elements that complement Belmont's diverse architectural heritage and sophisticated aesthetic.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <Zap className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Open Living</h3>
              <p className="text-muted-foreground">
                Signature open floor plans that maximize space and light while creating flowing, functional living environments.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <Sun className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Natural Light</h3>
              <p className="text-muted-foreground">
                Expansive windows and skylights that celebrate Belmont's beautiful climate while reducing energy consumption.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto prose prose-lg">
          <h2 className="text-3xl font-bold mb-6">Mid-Century Modern ADU Design Excellence in Belmont</h2>
          
          <p className="text-lg text-muted-foreground mb-6">
            Belmont's hillside location and moderate climate make it perfect for mid-century modern ADUs that emphasize the connection between interior and exterior spaces. Our designs capture the optimism and innovation of mid-century architecture while incorporating contemporary building standards and sustainability features.
          </p>

          <h3 className="text-2xl font-semibold mb-4">Mid-Century Modern Design Elements</h3>
          <ul className="space-y-2 text-muted-foreground mb-6">
            <li>• Floor-to-ceiling windows and sliding glass doors</li>
            <li>• Flat or low-pitched roofs with deep overhangs</li>
            <li>• Open floor plans with defined zones</li>
            <li>• Natural materials like wood and stone</li>
            <li>• Built-in furniture and storage solutions</li>
            <li>• Integration with outdoor living spaces</li>
          </ul>

          <h3 className="text-2xl font-semibold mb-4">Belmont Mid-Century Modern ADU Benefits</h3>
          <p className="text-muted-foreground mb-6">
            Mid-century modern ADUs in Belmont appeal to design-conscious residents and tech professionals who appreciate the style's emphasis on simplicity and functionality. These units offer excellent rental potential and significantly enhance property values in Belmont's competitive real estate market.
          </p>

          <div className="bg-muted/20 p-6 rounded-lg mb-8">
            <h4 className="text-xl font-semibold mb-3">Ready to Build Your Mid-Century Modern ADU in Belmont?</h4>
            <p className="text-muted-foreground mb-4">
              Our design team specializes in authentic mid-century modern principles while ensuring compliance with current Belmont building codes.
            </p>
            <Button className="group">
              Start Your Mid-Century Modern ADU Project
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Transform Your Belmont Property with Iconic Design</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Discover the enduring appeal of mid-century modern architecture in your custom Belmont ADU.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Free Mid-Century Modern ADU Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">Contact Our Design Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MidCenturyModernADUDesignBelmont;